/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';

import { useAppDispatch } from '../../app/redux/hooks';
import Price from '../../common/components/price/Price';
import AppLogger from '../../common/logger/AppLogger';
import { appSubscriptionService } from '../../common/subscriptions/appSubscriptionService';
import { PinEntryDevice, useConfirmPaymentFailedMutation, useLazyGetLastSuccessfulPaymentQuery } from '../api';
import { useOperatorSession } from '../appState';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';

import CardFailureConfirmationAmount from './CardFailureConfirmationAmount';
import CardFailureConfirmationAuthCode from './CardFailureConfirmationAuthCode';
import CardFailureConfirmationChoosePed from './CardFailureConfirmationChoosePed';
import './styles.sass';

export default function CardFailureConfirmationNewFlow() {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<any>();
  const [step, setStep] = useState(0);
  const [ped, setPed] = useState<PinEntryDevice | undefined>();
  const [amount, setAmount] = useState(0);
  const [authCode, setAuthCode] = useState('');
  const [validationError, setValidationError] = useState('');
  const [lastPaymentData, setLastPaymentData] = useState<any>();
  const [noLastPaymentData, setNoLastPaymentData] = useState(false);
  const operatorSession = useOperatorSession();

  const [triggerGetLastPayment] = useLazyGetLastSuccessfulPaymentQuery();
  const [triggerConfirmPaymentFailed] = useConfirmPaymentFailedMutation();

  useEffect(() => {
    const sub = appSubscriptionService.errorButtonClickedObservable().subscribe((val: any) => {
      if (val && val.type === 'card-failure') {
        console.warn('Starting new card failure confirmation flow', val);
        setState(val);
        setStep(1);
      }
    });
    return () => sub.unsubscribe();
  }, [setState]);

  const onCancelWindow = () => {
    // Re-push the error dialog with the original state to allow restarting the flow
    if (state) {
      const priceFormatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      });

      dispatch(
        showErrorDialog({
          errorType: 'card-payment-failure',
          message: state.message,
          dismissible: true,
          buttonText: 'Payment Failed',
          backgroundColor: 'red',
          alternateButtonText: 'Payment Completed',
          showAlternateButton: true,
          requireConfirmationOnClose: true,
          confirmNoText: 'Back',
          confirmYesText: 'Confirm Failed',
          confirmAlternateNoText: 'Back',
          confirmAlternateYesText: 'Enter Auth Code',
          confirmationText: `Are you sure the payment failed?<br /><br /> Please confirm you checked the ${
            state.pedName
          } card machine and have proof the payment declined for ${priceFormatter.format(state.amount)}`,
          confirmationTextAlternate: `Please confirm the payment succeeded,<br /><br /> You will need to enter the Auth Code off the card machine.<br /><br /><b>Amount:</b> ${priceFormatter.format(
            state.amount,
          )}<br /><b>Card Machine:</b> ${state.pedName}`,
          state: {
            transactionId: state.transactionId,
            transactionNumber: state.transactionNumber,
            sequenceNumber: state.sequenceNumber,
            operatorName: state.operatorName,
            operatorId: state.operatorId,
            amount: state.amount,
            reason: state.reason,
            cardRequestId: state.cardRequestId,
            message: state.message,
            pedName: state.pedName,
            pedTerminalId: state.pedTerminalId,
            type: 'card-failure',
          },
          alternateState: {
            transactionId: state.transactionId,
            transactionNumber: state.transactionNumber,
            sequenceNumber: state.sequenceNumber,
            operatorName: state.operatorName,
            operatorId: state.operatorId,
            amount: state.amount,
            reason: state.reason,
            cardRequestId: state.cardRequestId,
            message: state.message,
            pedName: state.pedName,
            pedTerminalId: state.pedTerminalId,
            type: 'offline-card',
          },
        }),
      );
    }

    setState(undefined);
    setStep(0);
    setPed(undefined);
    setAmount(0);
    setAuthCode('');
    setValidationError('');
    setLastPaymentData(undefined);
    setNoLastPaymentData(false);
  };

  const handlePedSelected = (pedGot: PinEntryDevice | null) => {
    if (pedGot) {
      setPed(pedGot);
      // Fetch last payment data
      triggerGetLastPayment({ pinEntryDeviceId: pedGot.id, cardRequestId: state?.cardRequestId })
        .unwrap()
        .then((data) => {
          AppLogger.info('Retrieved last successful payment data', JSON.stringify(data));
          setLastPaymentData(data);
          setNoLastPaymentData(false);
          setStep(2); // Move to amount entry
        })
        .catch((err) => {
          if (err.status === 417) {
            // No payment data available - accept any input
            setNoLastPaymentData(true);
            setLastPaymentData(undefined);
            setStep(2);
          } else {
            AppLogger.error(err);
            dispatch(
              showErrorDialog({
                message: 'Failed to retrieve last payment information',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
            onCancelWindow();
          }
        });
    } else onCancelWindow();
  };

  const handleAmountConfirmed = (enteredAmount: number) => {
    setAmount(enteredAmount);
    setStep(3); // Move to auth code entry
  };

  const handleAuthCodeConfirmed = (enteredAuthCode: string) => {
    setAuthCode(enteredAuthCode);

    // Validate if we have last payment data
    if (!noLastPaymentData && lastPaymentData) {
      const amountMatch = Math.abs(lastPaymentData.amount - amount) < 0.01; // Allow for floating point precision
      const authCodeMatch = lastPaymentData.authCode.toUpperCase() === enteredAuthCode.toUpperCase();

      if (!amountMatch || !authCodeMatch) {
        setValidationError(
          `We are unable to verify that the payment details entered match what we expect.\n\n` +
            `Please check the card machine again and re-enter the correct information.`,
        );
        setStep(4); // Show validation error
        return;
      }
    }

    // Validation passed or no data to validate against
    setStep(5); // Move to confirmation
  };

  const handleConfirm = () => {
    if (ped && state) {
      triggerConfirmPaymentFailed({
        transactionId: state.transactionId,
        request: {
          cardRequestId: state.cardRequestId,
          operatorId: operatorSession?.operator.id ?? 0,
          lastAuthCode: authCode,
          lastSuccessIntentId: lastPaymentData?.paymentIntentId ?? '',
          lastSuccessPaymentId: lastPaymentData?.paymentId ?? '',
          lastAuthorized: lastPaymentData?.authorized ?? false,
          lastAmount: amount,
        },
      })
        .unwrap()
        .then(() => {
          setState(undefined);
          setStep(0);
          setPed(undefined);
          setAmount(0);
          setAuthCode('');
          setValidationError('');
          setLastPaymentData(undefined);
          setNoLastPaymentData(false);
        })
        .catch((err) => {
          AppLogger.error(err);
          if (err.status === 400) {
            dispatch(
              showErrorDialog({
                message: err.data.message,
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          } else if (err.status === 403) {
            dispatch(
              showErrorDialog({
                message: 'Only the operator who performed the original transaction can confirm the failure',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          } else {
            dispatch(
              showErrorDialog({
                message: 'Failed to confirm payment failure',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          }
        });
    }
  };

  const handleRetry = () => {
    setValidationError('');
    setAmount(0);
    setAuthCode('');
    setStep(2); // Go back to amount entry
  };

  if (state == undefined) return <></>;

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') onCancelWindow();
        }}
      >
        {step === 1 && <CardFailureConfirmationChoosePed selectedPed={handlePedSelected} txPedTerminalId={state.pedTerminalId} />}

        {step === 2 && <CardFailureConfirmationAmount onAmountConfirmed={handleAmountConfirmed} />}

        {step === 3 && <CardFailureConfirmationAuthCode onAuthCodeConfirmed={handleAuthCodeConfirmed} />}

        {step === 4 && (
          <div className="pos-modal pos-modal_role_new-table">
            <div className="pos-modal__content">
              <div className="pos-new-table pos-pin-entry-devices">
                <div className="pos-new-table__header">
                  <div className="pos-pin-entry-devices__header">
                    <h2 className="pos-pin-entry-devices__title" style={{ color: 'red' }}>
                      Validation Error
                    </h2>
                    <p
                      className="pos-pin-entry-devices__subtitle"
                      style={{ whiteSpace: 'pre-line', fontSize: '1.2rem', marginTop: '1rem' }}
                    >
                      {validationError}
                    </p>
                  </div>
                </div>
                <div className="pos-new-table__actions">
                  <button className="pos-new-table__action" type="button" onClick={handleRetry}>
                    Try Again
                  </button>
                  <button
                    className="pos-new-table__action"
                    type="button"
                    onClick={onCancelWindow}
                    style={{ backgroundColor: '#666', marginLeft: '10px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="pos-modal pos-modal_role_new-table">
            <div className="pos-modal__content">
              <div className="pos-new-table pos-pin-entry-devices">
                <div className="pos-new-table__header">
                  <div className="pos-pin-entry-devices__header">
                    <h2 className="pos-pin-entry-devices__title">Confirm Payment Failure</h2>
                    <p className="pos-pin-entry-devices__subtitle">
                      Please confirm the following information is correct:
                      <br />
                      <br />
                      <strong>Card Machine:</strong> {ped?.name}
                      <br />
                      <strong>Last Successful Amount:</strong> <Price price={amount} />
                      <br />
                      <strong>Auth Code:</strong> {authCode}
                      <br />
                      <br />
                      {noLastPaymentData && (
                        <span style={{ color: 'orange' }}>
                          Note: No previous payment data was available for validation. Please ensure the information above is correct.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="pos-new-table__actions">
                  <button className="pos-new-table__action" type="button" onClick={handleConfirm}>
                    Confirm
                  </button>
                  <button
                    className="pos-new-table__action"
                    type="button"
                    onClick={handleRetry}
                    style={{ marginLeft: '10px', backgroundColor: '#666' }}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
